import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { action, makeObservable, observable, reaction, runInAction } from "mobx";
import { DateTime } from "luxon";
import { GatedFeature, GatedFeatureConfig, PlanId } from "@/store/feature-gates/types";
import { PaywallContext } from "@/domains/metrics/tracked-event";
import { buildFeatureGateConfigFromData, getPeriodWindow } from "@/store/feature-gates/utils";
import { GATED_FEATURE_DEFAULTS } from "@/store/feature-gates/defaults";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";

export class AppStoreFeatureGatesStore extends AppSubStore {
  upsellBannerVisible: boolean = false;
  upsellBannerExceeded: boolean = false;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<AppStoreFeatureGatesStore, "getGateConfig">(this, {
      useInitializeAppStoreEffects: false,
      openNoteCreationUpsell: false,
      openChatMessageUpsell: false,
      checkGate: false,
      openUsageLimitUpsell: action,
      closeUpsell: action,
      getGateConfig: false,
      upsellBannerVisible: observable,
      upsellBannerExceeded: observable,
      refreshUpsellBanner: action,
    });
  }

  useInitializeAppStoreEffects() {
    useEffectOnMount(() => {
      this.refreshUpsellBanner();
      const reactionDisposers = [
        reaction(
          () => this.store.notes.queuesAreActive,
          () => {
            this.refreshUpsellBanner();
          }
        ),

        reaction(
          () => this.store.chatMessages.sortedChatMessageIndexes.length,
          () => {
            this.refreshUpsellBanner();
          }
        ),

        reaction(
          () => this.store.subscriptions.personalSubscription?.tier,
          () => {
            this.refreshUpsellBanner();
          }
        ),
      ];
      return () => reactionDisposers.forEach(f => f());
    });
  }

  private getGateConfig(gateId: GatedFeature): GatedFeatureConfig {
    const storeConfig = this.store.platformInfo?.getFeatureGateConfig?.(gateId);
    if (!storeConfig) return GATED_FEATURE_DEFAULTS[gateId];

    return buildFeatureGateConfigFromData(storeConfig, gateId);
  }

  async checkGate(
    gateId: GatedFeature
  ): Promise<{ allowed: boolean; used: number; limit: number; warnAt: number | null }> {
    const subscription = await this.store.subscriptions.getPersonalSubscription();
    const planId: PlanId = (subscription?.tier as PlanId) || PlanId.Paid;
    const { limits, period, counter } = this.getGateConfig(gateId);
    const planLimit = limits[planId];

    const { periodStart, periodEnd } = getPeriodWindow(period, DateTime.local());
    const used = await counter({
      store: this.store,
      periodStart,
      periodEnd,
    });

    if (planLimit === null) {
      // no plan limit means unlimited
      return { allowed: true, used, limit: Infinity, warnAt: null };
    }

    return { allowed: used < planLimit, used, limit: planLimit, warnAt: limits.warning };
  }

  openNoteCreationUpsell(params: { used: number; limit: number }) {
    // Delegate to generic upsell
    this.openUsageLimitUpsell({ ...params, feature: GatedFeature.NoteCreate });
  }

  openChatMessageUpsell(params: { used: number; limit: number }) {
    // Delegate to generic upsell
    this.openUsageLimitUpsell({ ...params, feature: GatedFeature.ChatMessageSend });
  }

  openUsageLimitUpsell(params: {
    used: number;
    limit: number;
    feature: GatedFeature;
    context?: PaywallContext;
  }) {
    this.store.subscriptions.openUsageLimitUpsell(params);
  }

  closeUpsell = () => {
    this.store.subscriptions.closeUpsell();
  };

  /**
   * Computes and caches the banner visibility and exceeded state for note creation usage.
   */
  async refreshUpsellBanner() {
    const [notesResult, chatResult] = await Promise.all([
      this.checkGate(GatedFeature.NoteCreate),
      this.checkGate(GatedFeature.ChatMessageSend),
    ]);

    const notesWarn = Boolean(notesResult.warnAt && notesResult.used >= notesResult.warnAt);
    const chatWarn = Boolean(chatResult.warnAt && chatResult.used >= chatResult.warnAt);

    const notesExceeded = notesResult.used >= notesResult.limit;
    const chatExceeded = chatResult.used >= chatResult.limit;

    const isVisible = notesWarn || chatWarn || notesExceeded || chatExceeded;

    runInAction(() => {
      this.upsellBannerVisible = isVisible;
      this.upsellBannerExceeded = notesExceeded || chatExceeded;
    });
    if (!isVisible) {
      this.store.subscriptions.closeUpsell();
    }
  }
}
